import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useOutsideClickHandler } from '../../hooks/useOutsideClickHandler';
import { calculateOptimalPopupPosition, cn } from '../../lib/utils';
import {
  DropdownContext,
  DropdownContextType,
  DropdownPosition,
  DropdownProps,
} from '../../hooks/dropdownHooks';
import { MobileFullScreenModal } from './MobileFullScreenModal';

/* Re-export for backward compatibility */
export type { DropdownProps, DropdownContextType, DropdownPosition };
export { useDropdownContext } from '../../hooks/dropdownHooks';

/**
 * Dropdown component for displaying contextual content
 */
export function Dropdown({
  trigger,
  open,
  defaultOpen = false,
  onOpenChange,
  position = 'bottom-left',
  offset = { x: 0, y: 8 },
  width = 'auto',
  disablePositionOptimization = false,
  isMobile = false,
  contentClassName = '',
  closeOnItemClick = true,
  showBackdrop = false,
  anchorElement = null,
  mobilePosition = 0.5, // Default to center (0.5 = 50%)
  mobileTitle = 'Menu',
  children,
}: DropdownProps) {
  // State for controlled/uncontrolled behavior
  const [isOpenState, setIsOpenState] = useState(defaultOpen);
  const isOpen = open !== undefined ? open : isOpenState;

  // Refs for DOM elements
  const triggerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Calculate dropdown position
  const [dropdownPosition, setDropdownPosition] = useState<DropdownPosition>({ top: 0, left: 0 });
  // For mobile, we need to track if the dropdown's size has been measured
  const [isMeasured, setIsMeasured] = useState(false);

  // Handle outside clicks to close the dropdown using standardized hook
  // This handles a single ref at a time, so we need separate handlers for trigger and content
  useOutsideClickHandler(
    contentRef,
    event => {
      // Don't handle outside clicks on mobile - the MobileFullScreenModal has its own close button
      if (isMobile) {
        return;
      }

      // Make sure this wasn't a click on the trigger
      if (isOpen && triggerRef.current && !triggerRef.current.contains(event.target as Node)) {
        handleClose();
      }
    },
    isOpen,
    [triggerRef] // Exclude trigger ref from outside clicks
  );

  // Update parent component when open state changes
  const handleOpenChange = (newIsOpen: boolean) => {
    if (open === undefined) {
      setIsOpenState(newIsOpen);
    }

    onOpenChange?.(newIsOpen);
  };

  // Toggle dropdown open/closed
  const handleToggle = () => {
    handleOpenChange(!isOpen);
  };

  // Close the dropdown
  const handleClose = () => {
    handleOpenChange(false);
  };

  // Calculate optimal mobile position once the content is rendered and measured
  // This ensures we don't position dropdowns outside the viewport
  const calculateMobilePosition = () => {
    if (!contentRef.current) return null;

    const viewportHeight = window.innerHeight;
    const contentHeight = contentRef.current.offsetHeight;
    const safeAreaPadding = 20; // Padding to keep away from edges

    // Calculate the ideal position based on mobilePosition prop
    let idealTop = viewportHeight * mobilePosition;

    // Check if the dropdown would extend below the viewport
    const bottomOverflow = idealTop + contentHeight + safeAreaPadding - viewportHeight;
    if (bottomOverflow > 0) {
      // Adjust upward if needed
      idealTop = Math.max(safeAreaPadding, idealTop - bottomOverflow);
    }

    // Make sure we don't position too close to the top either
    idealTop = Math.max(safeAreaPadding, idealTop);

    return {
      top: idealTop,
      left: '50%',
      transform: 'translateX(-50%)',
      maxHeight: `${viewportHeight - safeAreaPadding * 2}px`,
    };
  };

  // Calculate position of dropdown based on trigger element or provided anchor
  const calculateDesktopPosition = () => {
    if (disablePositionOptimization) return;

    // Get the reference element - either the provided anchor, trigger ref, or nothing
    const referenceElement = anchorElement || triggerRef.current;

    if (!referenceElement) return;

    const rect = referenceElement.getBoundingClientRect();

    // Determine the origin point based on position prop
    const originPoint = { top: rect.bottom, left: rect.left };

    if (position.startsWith('top')) {
      originPoint.top = rect.top;
    } else if (position.startsWith('bottom')) {
      originPoint.top = rect.bottom;
    } else if (position.startsWith('left')) {
      originPoint.left = rect.left;
    } else if (position.startsWith('right')) {
      originPoint.left = rect.right;
    }

    // Adjust for alignment (e.g., 'top-right', 'bottom-left')
    if (position.endsWith('right')) {
      originPoint.left = rect.right;
    }

    // Get content dimensions for positioning
    const contentWidth = typeof width === 'number' ? width : contentRef.current?.offsetWidth || 300;
    const contentHeight = contentRef.current?.offsetHeight || 300;

    // Calculate optimal position
    const calculated = calculateOptimalPopupPosition(
      originPoint,
      { width: contentWidth, height: contentHeight },
      offset
    );

    setDropdownPosition({
      top: calculated.top,
      left: calculated.left,
    });
  };

  // Calculate position when dropdown opens or window resizes
  useEffect(() => {
    if (isOpen) {
      if (!isMobile) {
        calculateDesktopPosition();
      }
      window.addEventListener('resize', handleResize);
    } else {
      window.removeEventListener('resize', handleResize);
      setIsMeasured(false); // Reset measurement flag when closed
    }

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isOpen, anchorElement, isMobile]);

  // Handle resize events
  const handleResize = () => {
    if (isMobile) {
      setIsMeasured(false); // Force re-measurement on resize
    } else {
      calculateDesktopPosition();
    }
  };

  // After first render with content ref, measure mobile positioning
  useEffect(() => {
    if (isOpen && isMobile && contentRef.current && !isMeasured) {
      // Set a small timeout to ensure content has rendered completely
      const timer = setTimeout(() => {
        setIsMeasured(true);
      }, 10);

      return () => clearTimeout(timer);
    }
  }, [isOpen, isMobile, contentRef.current]);

  // Content styles
  const getContentStyles = (): React.CSSProperties => {
    // For mobile, calculate position based on content height
    if (isMobile) {
      if (contentRef.current && isMeasured) {
        const mobileStyles = calculateMobilePosition();
        // Calculate minimum width as 70% of viewport but respect custom width if larger
        const minWidth = `${Math.max(70, typeof width === 'number' ? (width / window.innerWidth) * 100 : 0)}%`;

        return {
          position: 'fixed',
          maxWidth: showBackdrop ? '100%' : '90%',
          minWidth: showBackdrop ? '100%' : minWidth,
          width: showBackdrop ? '100%' : typeof width === 'number' ? `${width}px` : width,
          height: showBackdrop ? '100%' : 'auto',
          zIndex: 1100,
          top: showBackdrop ? 0 : mobileStyles?.top,
          left: showBackdrop ? 0 : mobileStyles?.left,
          transform: showBackdrop ? 'none' : mobileStyles?.transform,
          borderRadius: showBackdrop ? 0 : undefined,
        };
      }

      // Initial position before measurement
      // Calculate minimum width as 70% of viewport but respect custom width if larger
      const minWidth = `${Math.max(70, typeof width === 'number' ? (width / window.innerWidth) * 100 : 0)}%`;

      return {
        position: 'fixed',
        top: showBackdrop ? 0 : '50%',
        left: showBackdrop ? 0 : '50%',
        transform: showBackdrop ? 'none' : 'translate(-50%, -50%)',
        maxWidth: showBackdrop ? '100%' : '90%',
        minWidth: showBackdrop ? '100%' : minWidth,
        width: showBackdrop ? '100%' : typeof width === 'number' ? `${width}px` : width,
        height: showBackdrop ? '100%' : 'auto',
        zIndex: 1100,
        opacity: isMeasured ? 1 : 0, // Hide until measured
        borderRadius: showBackdrop ? 0 : undefined,
      };
    }

    // Desktop positioning
    return {
      position: 'fixed',
      top: `${dropdownPosition.top}px`,
      left: `${dropdownPosition.left}px`,
      width: typeof width === 'number' ? `${width}px` : width,
      zIndex: 1000,
    };
  };

  return (
    <DropdownContext.Provider
      value={{
        isOpen,
        setIsOpen: handleOpenChange,
        isMobile,
        closeOnItemClick,
        handleClose,
      }}
    >
      {/* Trigger Element */}
      <div ref={triggerRef} onClick={handleToggle} className="inline-block">
        {trigger}
      </div>

      {/* Dropdown Content - Portaled to body */}
      {isOpen &&
        (isMobile && showBackdrop ? (
          <MobileFullScreenModal
            isOpen={true}
            onClose={handleClose}
            title={mobileTitle}
            footerContent={
              <button
                onClick={handleClose}
                className={cn(
                  'w-full py-2.5 px-4 bg-blue-500 text-white font-medium rounded-md hover:bg-blue-600 transition-colors',
                  isMobile && 'text-lg'
                )}
              >
                Done
              </button>
            }
          >
            {children}
          </MobileFullScreenModal>
        ) : (
          ReactDOM.createPortal(
            <>
              {/* Backdrop for mobile (only used for non-fullscreen mobile dropdowns) */}
              {showBackdrop && isMobile && (
                <div
                  className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[1050]"
                  onClick={handleClose}
                />
              )}

              {/* Dropdown Content */}
              <div
                ref={contentRef}
                style={getContentStyles()}
                className={cn(
                  'bg-white shadow-lg overflow-hidden border-0 animate-dropdown-fade',
                  isMobile && !showBackdrop ? 'max-w-sm rounded-lg' : '',
                  'rounded-lg shadow-xl',
                  contentClassName
                )}
                onClick={e => e.stopPropagation()}
              >
                {children}
              </div>
            </>,
            document.body
          )
        ))}
    </DropdownContext.Provider>
  );
}
